import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginUser,
  loginError,
  acceptInvitation,
  saveNextPath,
  fetchInvitation,
  logoutUser,
} from '../../AuthActions';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import Layout from '../../../App/components/Layout/LayoutContainer';
import PasswordStrengthIndicator from '../../../App/components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import { getProfile, isLoggedIn } from '../../AuthReducer';
import styles from './AcceptInvitationPage.css';

const messages = defineMessages({
  title: {
    id: 'AcceptInvitation.Title',
    defaultMessage: 'Join {orgname}',
  },
  subtitle: {
    id: 'AcceptInvitation.Subtitle',
    defaultMessage: '{inviter} invited you to {orgname} in Promoter Ninja.',
  },
  loading: {
    id: 'AcceptInvitation.Loading',
    defaultMessage: 'Loading invitation...',
  },
  error: {
    id: 'AcceptInvitation.Error',
    defaultMessage: 'Error loading invitation',
  },
  invitationExpired: {
    id: 'AcceptInvitation.Expired',
    defaultMessage: 'This invitation has expired',
  },
  invitationAlreadyAccepted: {
    id: 'AcceptInvitation.AlreadyAccepted',
    defaultMessage: 'This invitation has already been accepted',
  },
  invitationNotFound: {
    id: 'AcceptInvitation.NotFound',
    defaultMessage: 'Invitation not found',
  },
  organizationName: {
    id: 'AcceptInvitation.OrganizationName',
    defaultMessage: 'Organization',
  },
  role: {
    id: 'AcceptInvitation.Role',
    defaultMessage: 'Role',
  },
  invitedBy: {
    id: 'AcceptInvitation.InvitedBy',
    defaultMessage: 'Invited by',
  },
  fullNameLabel: {
    id: 'AcceptInvitation.FullNameLabel',
    defaultMessage: 'Your full name',
  },
  fullNamePlaceholder: {
    id: 'AcceptInvitation.FullNamePlaceholder',
    defaultMessage: 'E.g. Tom Smith',
  },
  passwordLabel: {
    id: 'AcceptInvitation.PasswordLabel',
    defaultMessage: 'Create a password',
  },
  passwordPlaceholder: {
    id: 'AcceptInvitation.PasswordPlaceholder',
    defaultMessage: 'Choose a strong password',
  },
  acceptButton: {
    id: 'AcceptInvitation.AcceptButton',
    defaultMessage: 'Create Account',
  },
  acceptButtonLoggedIn: {
    id: 'AcceptInvitation.AcceptButtonLoggedIn',
    defaultMessage: 'Accept Invitation',
  },
  acceptSuccess: {
    id: 'AcceptInvitation.Success',
    defaultMessage: 'Invitation accepted successfully!',
  },
  acceptError: {
    id: 'AcceptInvitation.AcceptError',
    defaultMessage: 'Error accepting invitation',
  },
  loginRequired: {
    id: 'AcceptInvitation.LoginRequired',
    defaultMessage: 'You need to be logged in to accept this invitation',
  },
  loginButton: {
    id: 'AcceptInvitation.LoginButton',
    defaultMessage: 'Log In',
  },
  signupButton: {
    id: 'AcceptInvitation.SignupButton',
    defaultMessage: 'Sign Up',
  },
  redirectingMessage: {
    id: 'AcceptInvitation.Redirecting',
    defaultMessage: 'Redirecting to team settings...',
  },
  emailVerificationRequired: {
    id: 'AcceptInvitation.EmailVerificationRequired',
    defaultMessage: 'Email Verification Required',
  },
  emailVerificationMessage: {
    id: 'AcceptInvitation.EmailVerificationMessage',
    defaultMessage:
      "You're already logged in, but we need to verify that this invitation was sent to your email address. Please check your email for a verification link or contact the organization administrator.",
  },
  // Role display names
  roleOwner: {
    id: 'AcceptInvitation.Role.Owner',
    defaultMessage: 'Owner',
  },
  roleAdmin: {
    id: 'AcceptInvitation.Role.Admin',
    defaultMessage: 'Admin',
  },
  roleMember: {
    id: 'AcceptInvitation.Role.Member',
    defaultMessage: 'Member',
  },
  roleViewer: {
    id: 'AcceptInvitation.Role.Viewer',
    defaultMessage: 'Viewer',
  },
  // Error messages
  invitationNotFoundMessage: {
    id: 'AcceptInvitation.NotFoundMessage',
    defaultMessage: "The invitation you're looking for doesn't exist or has been removed.",
  },
  genericErrorMessage: {
    id: 'AcceptInvitation.GenericErrorMessage',
    defaultMessage: 'Something went wrong while loading the invitation. Please try again.',
  },
  alreadyAcceptedMessage: {
    id: 'AcceptInvitation.AlreadyAcceptedMessage',
    defaultMessage:
      'This invitation has already been accepted. You can access the organization from your dashboard.',
  },
  expiredCancelledMessage: {
    id: 'AcceptInvitation.ExpiredCancelledMessage',
    defaultMessage:
      'This invitation has expired or been cancelled. Please contact the organization administrator for a new invitation.',
  },
  acceptErrorMessage: {
    id: 'AcceptInvitation.AcceptErrorMessage',
    defaultMessage:
      'There was an error creating your account or accepting the invitation. Please try again.',
  },
  // Loading states
  joiningText: {
    id: 'AcceptInvitation.Joining',
    defaultMessage: 'Joining...',
  },
  // Error fallback message
  failedToAcceptInvitation: {
    id: 'AcceptInvitation.FailedToAccept',
    defaultMessage: 'Failed to accept invitation',
  },
  or: {
    id: 'AcceptInvitation.Or',
    defaultMessage: 'or',
  },
  emailMismatchTitle: {
    id: 'AcceptInvitation.EmailMismatch.Title',
    defaultMessage: 'Email Mismatch',
  },
  emailMismatchMessage: {
    id: 'AcceptInvitation.EmailMismatch.Message',
    defaultMessage:
      'This invitation was sent to {invitationEmail}, but you are currently logged in as {currentEmail}.',
  },
  emailMismatchSimpleMessage: {
    id: 'AcceptInvitation.EmailMismatch.SimpleMessage',
    defaultMessage:
      "You're currently logged in as <b>{currentEmail}</b>. Please sign in (or signup) with <b>{invitationEmail}</b> to accept this invite.",
  },
  emailMismatchDescription: {
    id: 'AcceptInvitation.EmailMismatch.Description',
    defaultMessage: 'To accept this invitation, you need to sign out and either:',
  },
  emailMismatchOption1: {
    id: 'AcceptInvitation.EmailMismatch.Option1',
    defaultMessage: 'Sign in with {invitationEmail}',
  },
  emailMismatchOption2: {
    id: 'AcceptInvitation.EmailMismatch.Option2',
    defaultMessage: 'Create a new account with {invitationEmail}',
  },
  emailMismatchInstruction: {
    id: 'AcceptInvitation.EmailMismatch.Instruction',
    defaultMessage: 'You need to sign out and then log in or sign up using {invitationEmail}',
  },
  signOutButton: {
    id: 'AcceptInvitation.SignOut',
    defaultMessage: 'Sign Out',
  },
  accountExistsTitle: {
    id: 'AcceptInvitation.AccountExists.Title',
    defaultMessage: 'Account Already Exists',
  },
  accountExistsMessage: {
    id: 'AcceptInvitation.AccountExists.Message',
    defaultMessage:
      'An account with {email} already exists. You can sign in with that account to accept this invitation.',
  },
  accountExistsAction: {
    id: 'AcceptInvitation.AccountExists.Action',
    defaultMessage: 'Sign in with your existing account to accept this invitation.',
  },
});

function AcceptInvitationPage() {
  const params = useParams();
  const { invitationId } = params;
  const [searchParams] = useSearchParams();
  const token = params.token || searchParams.get('token');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const isLoggedInUser = useSelector(isLoggedIn);
  const currentUser = useSelector(getProfile);

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    password: '',
  });

  useEffect(() => {
    const loadInvitation = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await dispatch(fetchInvitation(invitationId, token));
        setInvitation(data.invitation);
      } catch (err) {
        console.error('Error fetching invitation:', err);
        if (err.status === 404) {
          setError('notFound');
        } else if (err.bodyJson?.error === 'This invitation is not for your email address') {
          setError('emailMismatch');
          setInvitation(err.invitation);
        } else if (err.bodyJson?.error === 'Invitation has expired or is no longer valid') {
          setError('expired');
        } else {
          setError('error');
        }
      } finally {
        setLoading(false);
      }
    };

    if (invitationId && token) {
      loadInvitation();
    } else {
      setError('error');
      setLoading(false);
    }
  }, [invitationId, token, currentUser.id, dispatch]);

  const handleInputChange = field => event => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleGoogleLogin = googleResponse => {
    // Save current path before login
    dispatch(saveNextPath(window.location.pathname + window.location.search));
    dispatch(loginUser({ credential: googleResponse.credential }, true, navigate.replace));
  };

  const handleAcceptInvitation = async () => {
    if (!formData.fullName.trim() || !formData.password.trim()) {
      return;
    }

    setAccepting(true);
    try {
      const data = {
        fullName: formData.fullName.trim(),
        password: formData.password,
        email: invitation.email,
      };

      const _result = await dispatch(acceptInvitation(invitationId, token, data));
      setAccepted(true);
      // Redirect to settings after a short delay
      setTimeout(() => {
        navigate('/settings/team');
      }, 2000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      if (err.bodyJson?.error === 'This invitation is not for your email address') {
        setError('emailMismatch');
      } else if (err.bodyJson?.error === 'An account with this email already exists') {
        setError('accountExists');
      } else {
        setError('acceptError');
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleAcceptInvitationLoggedIn = async () => {
    setAccepting(true);
    try {
      const _result = await dispatch(acceptInvitation(invitationId, token));
      setAccepted(true);
      // Redirect to settings after a short delay
      setTimeout(() => {
        navigate('/settings/team');
      }, 2000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      if (err.bodyJson?.error === 'This invitation is not for your email address') {
        setError('emailMismatch');
      } else if (err.bodyJson?.error === 'An account with this email already exists') {
        setError('accountExists');
      } else {
        setError('acceptError');
      }
    } finally {
      setAccepting(false);
    }
  };

  const getRoleDisplayName = role => {
    const roleMessages = {
      owner: formatMessage(messages.roleOwner),
      admin: formatMessage(messages.roleAdmin),
      member: formatMessage(messages.roleMember),
      viewer: formatMessage(messages.roleViewer),
    };
    return roleMessages[role] || role;
  };

  // Common styles to avoid repetition
  const commonPaperStyles = {
    padding: '2em',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
  };

  const formPaperStyles = {
    padding: '2.5em',
    width: '100%',
    maxWidth: '500px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
  };

  // Helper function to render alert content
  const renderAlert = (severity, title, message) => (
    <Paper sx={commonPaperStyles}>
      <Alert severity={severity} sx={{ mb: 2 }}>
        {title}
      </Alert>
      <Typography variant="body1" sx={{ textAlign: 'left' }}>
        {message}
      </Typography>
    </Paper>
  );

  // Determine what content to render based on current state
  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: '#1976d2', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {formatMessage(messages.loading)}
          </Typography>
        </Box>
      );
    }

    if (error === 'notFound') {
      return renderAlert(
        'error',
        formatMessage(messages.invitationNotFound),
        formatMessage(messages.invitationNotFoundMessage),
      );
    }

    if (error === 'expired') {
      return renderAlert(
        'warning',
        formatMessage(messages.invitationExpired),
        formatMessage(messages.expiredCancelledMessage),
      );
    }

    if (error === 'error') {
      return renderAlert(
        'error',
        formatMessage(messages.error),
        formatMessage(messages.genericErrorMessage),
      );
    }

    if (!invitation) {
      return null;
    }

    if (invitation.status === 'accepted') {
      return renderAlert(
        'info',
        formatMessage(messages.invitationAlreadyAccepted),
        formatMessage(messages.alreadyAcceptedMessage),
      );
    }

    if (invitation.status === 'expired' || invitation.status === 'cancelled') {
      return renderAlert(
        'warning',
        formatMessage(messages.invitationExpired),
        formatMessage(messages.expiredCancelledMessage),
      );
    }

    if (accepted) {
      return renderAlert(
        'success',
        formatMessage(messages.acceptSuccess),
        formatMessage(messages.redirectingMessage),
      );
    }

    if (error === 'acceptError') {
      return renderAlert(
        'error',
        formatMessage(messages.acceptError),
        formatMessage(messages.acceptErrorMessage),
      );
    }

    if (error === 'emailMismatch') {
      return (
        <Paper sx={commonPaperStyles}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {formatMessage(messages.emailMismatchTitle)}
          </Alert>

          <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
            <FormattedMessage
              id="AcceptInvitation.EmailMismatch.SimpleMessage"
              values={{
                b: chunks => <strong>{chunks}</strong>,
                currentEmail: currentUser?.email || '',
              }}
            />
          </Typography>

          <Button
            variant="outlined"
            onClick={() => {
              dispatch(logoutUser());
            }}
            fullWidth
          >
            {formatMessage(messages.signOutButton)}
          </Button>
        </Paper>
      );
    }

    if (error === 'accountExists') {
      return (
        <Paper sx={commonPaperStyles}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {formatMessage(messages.accountExistsTitle)}
          </Alert>

          <Typography variant="body1" sx={{ mb: 2, textAlign: 'left' }}>
            <FormattedMessage
              id="AcceptInvitation.AccountExists.Message"
              values={{
                email: invitation.email,
              }}
            />
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'left', fontWeight: 500 }}>
            {formatMessage(messages.accountExistsAction)}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              dispatch(logoutUser());
            }}
            fullWidth
          >
            {formatMessage(messages.signOutButton)}
          </Button>
        </Paper>
      );
    }

    // If user is already logged in, show accept button
    if (isLoggedInUser) {
      return (
        <Paper sx={commonPaperStyles}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              {formatMessage(messages.title, {
                orgname: invitation?.organizationName || 'Organization',
              })}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {formatMessage(messages.subtitle, {
                inviter: invitation?.inviterName || 'Someone',
                orgname: invitation?.organizationName || 'Organization',
                role: getRoleDisplayName(invitation?.role || 'member'),
              })}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={handleAcceptInvitationLoggedIn}
            disabled={accepting}
            startIcon={accepting ? <CircularProgress size={20} /> : null}
            fullWidth
          >
            {accepting
              ? formatMessage(messages.joiningText)
              : formatMessage(messages.acceptButtonLoggedIn)}
          </Button>
        </Paper>
      );
    }

    // If user is not logged in, show the form to create account
    // This is the expected flow for invitation acceptance
    if (!isLoggedInUser) {
      return (
        <Paper sx={formPaperStyles}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
              {formatMessage(messages.title, {
                orgname: invitation?.organizationName || 'Organization',
              })}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {formatMessage(messages.subtitle, {
                inviter: invitation?.inviterName || 'Someone',
                orgname: invitation?.organizationName || 'Organization',
                role: getRoleDisplayName(invitation?.role || 'member'),
              })}
            </Typography>
          </Box>

          <form
            onSubmit={e => {
              e.preventDefault();
              handleAcceptInvitation();
            }}
            autoComplete="off"
            data-form-type="signup"
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Hidden email field to help password managers recognize this as a signup form */}
              <input
                type="email"
                name="email"
                autoComplete="email"
                style={{ display: 'none' }}
                aria-hidden="true"
              />

              <TextField
                autoFocus
                fullWidth
                label={formatMessage(messages.fullNameLabel)}
                placeholder={formatMessage(messages.fullNamePlaceholder)}
                value={formData.fullName}
                onChange={handleInputChange('fullName')}
                variant="outlined"
                size="medium"
                name="fullName"
                autoComplete="name"
                inputProps={{
                  'data-lpignore': 'false',
                  'data-form-type': 'signup',
                }}
              />

              <TextField
                fullWidth
                label={formatMessage(messages.passwordLabel)}
                placeholder={formatMessage(messages.passwordPlaceholder)}
                type="password"
                value={formData.password}
                onChange={handleInputChange('password')}
                variant="outlined"
                size="medium"
                name="newPassword"
                autoComplete="new-password"
                inputProps={{
                  'data-lpignore': 'false',
                  'data-form-type': 'signup',
                }}
              />

              <PasswordStrengthIndicator
                password={formData.password}
                marginTop={-1}
                marginBottom={1}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={
                  accepting ||
                  !formData.fullName.trim() ||
                  !formData.password.trim() ||
                  formData.password.length < 8
                }
                startIcon={accepting ? <CircularProgress size={20} /> : null}
                fullWidth
                sx={{ mt: 2 }}
              >
                {accepting
                  ? formatMessage(messages.joiningText)
                  : formatMessage(messages.acceptButton)}
              </Button>

              <Box
                sx={{
                  textAlign: 'center',
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <hr className={styles.hrText} data-content={formatMessage(messages.or)} />
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => console.log('Google Login Failed')}
                  useOneTap
                  theme="outline"
                  size="large"
                  text="signup_with"
                  shape="pill"
                  width="100%"
                />
              </Box>
            </Box>
          </form>
        </Paper>
      );
    }

    // Fallback: if we get here and no other condition matched, show the form
    return (
      <Paper sx={formPaperStyles}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
            {formatMessage(messages.title, {
              orgname: invitation?.organizationName || 'Organization',
            })}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {formatMessage(messages.subtitle, {
              inviter: invitation?.inviterName || 'Someone',
              orgname: invitation?.organizationName || 'Organization',
              role: getRoleDisplayName(invitation?.role || 'member'),
            })}
          </Typography>
        </Box>

        <form
          onSubmit={e => {
            e.preventDefault();
            handleAcceptInvitation();
          }}
          autoComplete="off"
          data-form-type="signup"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Hidden email field to help password managers recognize this as a signup form */}
            <input
              type="email"
              name="email"
              autoComplete="email"
              style={{ display: 'none' }}
              aria-hidden="true"
            />

            <TextField
              autoFocus
              fullWidth
              label={formatMessage(messages.fullNameLabel)}
              placeholder={formatMessage(messages.fullNamePlaceholder)}
              value={formData.fullName}
              onChange={handleInputChange('fullName')}
              variant="outlined"
              size="medium"
              name="fullName"
              autoComplete="name"
              inputProps={{
                'data-lpignore': 'false',
                'data-form-type': 'signup',
              }}
            />

            <TextField
              fullWidth
              label={formatMessage(messages.passwordLabel)}
              placeholder={formatMessage(messages.passwordPlaceholder)}
              type="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              variant="outlined"
              size="medium"
              name="newPassword"
              autoComplete="new-password"
              inputProps={{
                'data-lpignore': 'false',
                'data-form-type': 'signup',
              }}
            />

            <PasswordStrengthIndicator
              password={formData.password}
              marginTop={-1}
              marginBottom={1}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={
                accepting ||
                !formData.fullName.trim() ||
                !formData.password.trim() ||
                formData.password.length < 8
              }
              startIcon={accepting ? <CircularProgress size={20} /> : null}
              fullWidth
              sx={{ mt: 2 }}
            >
              {accepting
                ? formatMessage(messages.joiningText)
                : formatMessage(messages.acceptButton)}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formatMessage(messages.or)}
              </Typography>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => console.log('Google Login Failed')}
                useOneTap
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
                width="100%"
              />
            </Box>
          </Box>
        </form>
      </Paper>
    );
  };

  // Single return with conditional content
  return (
    <Layout
      title={formatMessage(messages.title, {
        orgname: invitation?.organizationName,
      })}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: invitation && !isLoggedInUser ? 'flex-start' : 'center',
          minHeight: 'calc(100vh - 200px)',
          padding: '20px 20px',
          margin: '0 -16px',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        }}
      >
        {renderContent()}
      </Box>
    </Layout>
  );
}

export default AcceptInvitationPage;
